/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import { debounce } from 'lodash';
import { compose } from 'recompose';

import user from 'js/lib/user';

// Components
import { Button, Divider, Grid, Radio, RadioGroup, TemporaryDrawer, TextField, breakpoints } from '@coursera/cds-core';
import { TrashIcon } from '@coursera/cds-icons';
import type {
  DeleteAdminUserFilterRequest,
  DeleteAdminUserFilterResponse,
  SearchAdminUserFilterRequest,
  SearchAdminUserFilterResponse,
} from '@coursera/grpc-types-entsavedfilters/coursera/proto/entsavedfilters/v1beta1/enterprise_saved_filters_api';

import { TrackedCdsButton } from 'bundles/common/components/withSingleTracked';
import { getFilterName, getPresetFilters } from 'bundles/enterprise-admin-user-filters/constants';
import { useUserFilter } from 'bundles/enterprise-admin-user-filters/context/userFilterContext';
import type { SavedAdminFilter } from 'bundles/enterprise-admin-user-filters/types';
import { mapUserFilterFromServerToClientModel } from 'bundles/enterprise-admin-user-filters/utils';
import { Spacer } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-user-filters';

const { HeadingGroup, Content, Actions } = TemporaryDrawer;

type PropsFromCaller = {
  isOpen: boolean;
  programScope?: boolean;
  program?: {
    id: string;
    slug: string;
    metadata: { name: string };
  };
  organizationId: string;
  searchQuery: string;
  onClose: () => void;
  onSearchQueryChange: (query: string) => void;
  onChangeView: (filter: SavedAdminFilter) => void;
};

type PropsFromGraphql1 = {
  onDeleteAdminUserFilter: (request: {
    variables: {
      input: DeleteAdminUserFilterRequest;
    };
  }) => Promise<{ data: DeleteAdminUserFilterResponse }>;
};

type PropsFromGraphql2 = {
  savedFilters?: SavedAdminFilter[];
  refetchSavedFilters?: () => Promise<{}>;
};

type Props = PropsFromCaller & PropsFromGraphql1 & PropsFromGraphql2;

export const ChangeViewDrawer = ({
  isOpen,
  programScope,
  program,
  savedFilters = [],
  searchQuery,
  refetchSavedFilters,
  onClose,
  onSearchQueryChange,
  onDeleteAdminUserFilter,
  onChangeView,
}: Props) => {
  const presetFilters =
    programScope && program
      ? getPresetFilters({ programId: program.id, programName: program.metadata.name })
      : getPresetFilters();

  const { userFilter } = useUserFilter();

  const [selectedFilter, setSelectedFilter] = React.useState<(typeof presetFilters)[0]>(userFilter || presetFilters[0]);
  const [query, setQuery] = React.useState(searchQuery);
  // TODO: React Hook useCallback received a function whose dependencies are unknown. Pass an inline function instead.
  // FIXME: existing react-hooks/exhaustive-deps violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(debounce(onSearchQueryChange, 500), []);
  const handleSearch = (value: string) => {
    setQuery(value);
    debouncedSearch(value);
  };

  const handleLoadView = () => {
    onChangeView(selectedFilter);
    onClose();
  };

  const filteredPresets = presetFilters.filter((_) => _.filterName.toLowerCase().includes(searchQuery.toLowerCase()));
  const noViewSelected =
    savedFilters.every((_) => _.filterId !== selectedFilter.filterId) &&
    filteredPresets.every((_) => _.filterId !== selectedFilter.filterId);

  return (
    <TemporaryDrawer open={isOpen} anchor="right" onClose={onClose} sticky="actions">
      <HeadingGroup supportText={_t('Views are a collection of filters, useful for quick access.')}>
        {_t('Change view')}
      </HeadingGroup>
      <Content>
        <Spacer size={32} />
        <TextField
          id="search-saved-views"
          label={_t('Search saved views')}
          fullWidth
          placeholder={_t('Search by view name')}
          onChange={(event) => handleSearch(event.target.value)}
          data-testid="change-view-drawer-search-input"
          value={query}
          autoFocus
        />
        <Spacer size={32} />
        <RadioGroup
          label={_t('Views')}
          value={selectedFilter.filterId}
          css={{ width: '100%' }}
          onChange={(event) => {
            const { value } = event.target;
            const view = [...presetFilters, ...savedFilters].find((_) => _.filterId === value);
            if (view) setSelectedFilter(view);
          }}
          data-testid="change-view-drawer-radiogroup"
        >
          {[
            ...(filteredPresets.map((filter) => (
              <React.Fragment key={filter.filterId}>
                <Spacer size={4} />
                <Grid container justifyContent="space-between">
                  <Radio value={filter.filterId} label={getFilterName(filter, programScope)} />
                </Grid>
                <Spacer size={8} />
                <Divider color="light" />
              </React.Fragment>
            )) ?? []),
            ...(savedFilters?.map((filter) => (
              <React.Fragment key={filter.filterId}>
                <Spacer size={4} />
                <Grid container justifyContent="space-between">
                  <Radio value={filter.filterId} label={filter.filterName} />
                  <Button
                    variant="ghost"
                    size="small"
                    icon={<TrashIcon size="small" />}
                    onClick={async () => {
                      try {
                        await onDeleteAdminUserFilter({ variables: { input: { filterId: filter.filterId } } });
                      } catch {
                        // Network error: Cannot read properties of undefined (reading 'selections')
                        // this error is thrown by apollo, because the mutation does not return anything at all
                      } finally {
                        await refetchSavedFilters?.();
                      }
                    }}
                    data-testid="change-view-drawer-delete-button"
                  >
                    {_t('Delete view')}
                  </Button>
                </Grid>
                <Spacer size={8} />
                <Divider color="light" />
              </React.Fragment>
            )) ?? []),
          ]}
        </RadioGroup>
      </Content>
      <Actions
        css={css`
          display: flex;
          gap: var(--cds-spacing-200);
          ${breakpoints.down('xs')} {
            flex-direction: column;
          }
        `}
      >
        <TrackedCdsButton
          trackingName="change_view_drawer_load_view"
          trackingData={{
            filterId: selectedFilter.filterId,
          }}
          data-testid="change_view_drawer_load_view"
          css={css`
            ${breakpoints.down('xs')} {
              width: 100%;
              max-width: none;
            }
          `}
          variant="primary"
          onClick={handleLoadView}
          disabled={noViewSelected}
        >
          {_t('Load view')}
        </TrackedCdsButton>

        <Button
          css={css`
            ${breakpoints.down('xs')} {
              width: 100%;
              max-width: none;
            }
          `}
          variant="secondary"
          onClick={onClose}
          data-testid="change_view_drawer_cancel"
        >
          {_t('Cancel')}
        </Button>
      </Actions>
    </TemporaryDrawer>
  );
};

export const ChangeViewDrawerQuery = gql`
  query ChangeViewDrawerQuery($input: SearchAdminUserFilterRequest!) {
    ChangeViewDrawerQuery(input: $input)
      @rest(
        type: "SearchAdminUserFilter"
        path: "grpc/entsavedfilters/v1beta1/EnterpriseSavedFiltersAPI/SearchAdminUserFilter"
        method: "POST"
      ) {
      adminUserFilters
      nextPageToken
      totalSize
    }
  }
`;

export const DeleteAdminUserFilterMutation = gql`
  mutation DeleteAdminUserFilterMutation($input: DeleteAdminUserFilterRequest!) {
    DeleteAdminUserFilterMutation(input: $input)
      @rest(
        type: "DeleteAdminUserFilter"
        path: "grpc/entsavedfilters/v1beta1/EnterpriseSavedFiltersAPI/DeleteAdminUserFilter"
        method: "POST"
      ) {
      NoResponse
    }
  }
`;

export default compose<Props, PropsFromCaller>(
  graphql(DeleteAdminUserFilterMutation, {
    name: 'onDeleteAdminUserFilter',
  }),
  graphql<
    PropsFromCaller,
    { ChangeViewDrawerQuery: SearchAdminUserFilterResponse },
    { input: SearchAdminUserFilterRequest },
    PropsFromGraphql2
  >(ChangeViewDrawerQuery, {
    options: ({ organizationId, searchQuery }: PropsFromCaller) => ({
      variables: {
        input: {
          organizationId,
          filterType: 'FILTER_TYPE_USER_V1',
          filterName: searchQuery,
          createdBy: [user.get().id],
        } as SearchAdminUserFilterRequest,
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    }),
    props: ({ data, ownProps: { organizationId, searchQuery } }) => {
      return {
        savedFilters:
          data?.ChangeViewDrawerQuery?.adminUserFilters?.map((filter) => ({
            ...filter,
            filter: mapUserFilterFromServerToClientModel(filter),
          })) ?? [],
        refetchSavedFilters:
          data &&
          (() =>
            data.refetch({
              input: {
                organizationId,
                filterType: 'FILTER_TYPE_USER_V1',
                filterName: searchQuery,
                createdBy: [user.get().id],
              },
            })),
      };
    },
  })
)(ChangeViewDrawer);
